import ControlFactory from '@/Factories/ControlFactory';
import ComponentManager from '@/Management/ComponentManager';
import ComparisonControlT from '@/Components/ComparisonControl/ComparisonControl';
import ComparisonControlViewModelT from '@/Components/ComparisonControl/ComparisonControlViewModel';
import Factory from '@/Factories/Factory';
import type {
    IComparisonControlFactoryConstructorParameter,
    IComparisonControlViewModelConstructorParameter
} from "#/DefaultViewExtensionInterfaces";

export default class ComparisonControlFactory extends ControlFactory {
    protected ComparisonControl = ComponentManager.getControl<typeof ComparisonControlT>('ComparisonControl');
    protected ComparisonControlViewModel = ComponentManager.getViewModel<typeof ComparisonControlViewModelT>('ComparisonControl');

    protected originalUIGenerator: string;
    protected field: string;
    protected displayField: string;
    protected comparisonField: string;
    protected comparisonDisplayField: string;
    protected originalFactory: ControlFactory;

    constructor(params: IComparisonControlFactoryConstructorParameter) {
        super(params);

        if (params.hideLabel && !this.context.isSummaryViewMode())
            this.labelPosition = "None";

        this.originalUIGenerator = this.unescapeUIGenerator(params.escapedOriginalUIGenerator);
        this.field = this.paramOrDefault(params.field, this.detailViewItem.name);
        this.displayField = this.paramOrDefault(params.displayField, this.field);
        this.comparisonField = params.comparisonField;
        this.comparisonDisplayField = this.paramOrDefault(params.comparisonDisplayField, this.comparisonField);
        this.originalFactory = Factory.createInstance<ControlFactory>(this.originalUIGenerator, {
            detailViewItem: this.detailViewItem,
            context: this.context,
            locator: this.locator,
        });
        // Können manipuliert worden sein
        this.detailViewItem.name = this.originalFactory.detailViewItem.name;
        this.neighborOf = this.originalFactory.neighborOf;

        this.isEnabled = this.originalFactory.isEnabled;
        this.isVisible = this.originalFactory.isVisible;
    }

    public getControl(): JQuery<HTMLElement> {
        const viewModel = this.createViewModel();
        const control = this.createControl(viewModel);
        return control.getTuple().control;
    }

    protected createViewModel(): ComparisonControlViewModelT {
        const parameters = this.createViewModelParameters();
        return new this.ComparisonControlViewModel(parameters);
    }

    protected createViewModelParameters(): IComparisonControlViewModelConstructorParameter {
        const genericObject = this.context.genericObject;
        return {
            value: genericObject.getObservable(this.field),
            displayValue: genericObject.getObservable(this.displayField),
            comparisonValue: genericObject.getObservable(this.comparisonField),
            comparisonDisplayValue: genericObject.getObservable(this.comparisonDisplayField),
        };
    }

    protected createControl(viewModel: ComparisonControlViewModelT): ComparisonControlT {
        const $originalControl = this.originalFactory.getControl();
        return new this.ComparisonControl($originalControl, viewModel);
    }

    private static escapeUIGenerator(uiGenerator: string) {
        return uiGenerator
            .replace('&', '---AND---')
            .replace('?', '---Q---');
    }

    private unescapeUIGenerator(uiGenerator: string) {
        return uiGenerator
            .replace('---AND---', '&')
            .replace('---Q---', '?');
    }

    public static createUIGeneratorReplacer(field: string, displayField: string, comparisonField: string, comparisonDisplayField: string, hideLabel?: boolean): (uiGenerator: string) => string {
        return originalUIGenerator => {
            const parsedOriginalGenerator = Factory.parseUIGeneratorQuery(originalUIGenerator);
            const params: IComparisonControlFactoryConstructorParameter = {
                ...parsedOriginalGenerator.parameters,
                field,
                displayField: displayField || field,
                comparisonField,
                comparisonDisplayField: comparisonDisplayField || comparisonField,
                escapedOriginalUIGenerator: ComparisonControlFactory.escapeUIGenerator(originalUIGenerator),
                hideLabel
            };

            return Factory.toUIGeneratorQuery({
                uiGenerator: 'ComparisonControl',
                parameters: params
            });
        };
    }
}

ComponentManager.setFactory('ComparisonControl', ComparisonControlFactory, 'RADPresentation.HTMLClient.DefaultViewExtensions');